import {TranslationFunction, useTranslation} from '@aboutyou/i18n';
import React, {FC, type JSX} from 'react';
import styled from 'styled-components';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {Icon} from '../../atoms/Icon/Icon';
import {PushNotificationIconName} from './assets';

export type PushNotificationType =
  | 'a2w_l_push_trg'
  | 'a2w_s_push_trg'
  | 'osp_push_trg'
  | 'sale_push_trg'
  | 'long_session';

interface PushNotificationHeaderProps {
  type: PushNotificationType;
}

export const PushNotificationHeader: FC<PushNotificationHeaderProps> = ({
  type,
}) => {
  const {t} = useTranslation();
  const iconAssetName = getIconAssetName(type);
  const headerAndSubTitle = getHeaderAndSubTitle(type, t);

  return (
    <Header>
      <StyledIcon
        data-testid="PushNotificationHeaderIcon"
        asset={iconAssetName}
        size="L"
      />
      {headerAndSubTitle}
    </Header>
  );
};

const getIconAssetName = (
  type: PushNotificationType,
): PushNotificationIconName => {
  switch (type) {
    case 'a2w_l_push_trg':
      return PushNotificationIconName.PushBellowYellow;
    case 'osp_push_trg':
      return PushNotificationIconName.PushTruck;
    case 'sale_push_trg':
      return PushNotificationIconName.PushSale;
    case 'long_session':
      return PushNotificationIconName.PushBellBadge;
    case 'a2w_s_push_trg':
    default:
      return PushNotificationIconName.PushBellRed;
  }
};

const getHeaderAndSubTitle = (
  type: PushNotificationType,
  t: TranslationFunction,
): JSX.Element => {
  let headerText = '';

  switch (type) {
    case 'a2w_l_push_trg': {
      headerText = t('Favorite product out of stock?');
      break;
    }
    case 'osp_push_trg': {
      headerText = t('Where is my order?');
      break;
    }
    case 'sale_push_trg': {
      headerText = t('When is the next sale?');
      break;
    }
    case 'a2w_s_push_trg': {
      headerText = t('Never miss a sale again?');
      break;
    }
    case 'long_session': {
      headerText = t('Interested in more updates from ABOUT YOU?');
    }
  }

  return (
    <>
      <HeaderText>{headerText}</HeaderText>
      <HeaderSubtitle>
        {t('Benefit from all our push notifications in the future:')}
      </HeaderSubtitle>
    </>
  );
};

const StyledIcon = styled(Icon)`
  margin: 0 auto;
  fill: none;
`;

const Header = styled.section`
  padding: 0 1.25rem;
  text-align: center;
`;

const HeaderText = styled.h4`
  margin: 0.375rem 0 0;
  font-size: ${FONT_SIZES.px20};
  font-weight: ${FONT_WEIGHTS.bold};
`;

const HeaderSubtitle = styled.h5`
  margin: 0.5rem 0 0;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.light};
`;
